/**
 * <hh-sp-ingredient-list>
 *
 * Manages a single SP Ingredient List section instance:
 *  - Per-card "Learn More" toggle (shows/hides description overlay).
 *    Only one card can be active at a time within the section.
 *  - "See Full Ingredient List" button opens the section's modal.
 *    Closing the modal: overlay click, close button, or Escape key.
 *
 * Multiple instances are isolated via [data-section-id] / [data-modal-id].
 */

class HHSpIngredientList extends HTMLElement {
  constructor() {
    super()

    this.handleLearnClick = this.handleLearnClick.bind(this)
    this.handleCloseCard = this.handleCloseCard.bind(this)
    this.handleOpenModal = this.handleOpenModal.bind(this)
    this.handleCloseModal = this.handleCloseModal.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
  }

  connectedCallback() {
    this.modalId = this.dataset.modalId
    this.modal = this.modalId ? this.querySelector(`#${this.modalId}`) : null

    this.cards = Array.from(this.querySelectorAll('.js-sp-ingredient-card'))
    this.learnButtons = Array.from(
      this.querySelectorAll('.js-sp-ingredient-card-learn')
    )
    this.openButtons = Array.from(
      this.querySelectorAll('.js-sp-ingredient-list-open')
    )
    this.closeCardButtons = Array.from(
      this.querySelectorAll('.js-sp-ingredient-card-close')
    )
    this.closeElements = this.modal
      ? Array.from(this.modal.querySelectorAll('.js-sp-ingredient-full-list-close'))
      : []

    this.attachListeners()
  }

  disconnectedCallback() {
    this.removeListeners()
  }

  attachListeners() {
    this.learnButtons.forEach((btn) => {
      btn.addEventListener('click', this.handleLearnClick)
    })

    this.closeCardButtons.forEach((btn) => {
      btn.addEventListener('click', this.handleCloseCard)
    })

    this.openButtons.forEach((btn) => {
      btn.addEventListener('click', this.handleOpenModal)
    })

    this.closeElements.forEach((el) => {
      el.addEventListener('click', this.handleCloseModal)
    })
  }

  removeListeners() {
    this.learnButtons.forEach((btn) => {
      btn.removeEventListener('click', this.handleLearnClick)
    })

    this.closeCardButtons.forEach((btn) => {
      btn.removeEventListener('click', this.handleCloseCard)
    })

    this.openButtons.forEach((btn) => {
      btn.removeEventListener('click', this.handleOpenModal)
    })

    this.closeElements.forEach((el) => {
      el.removeEventListener('click', this.handleCloseModal)
    })

    document.removeEventListener('keydown', this.handleKeydown)
  }

  deactivateAllCards() {
    this.cards.forEach((c) => {
      c.classList.remove('is-active')
      const learnBtn = c.querySelector('.js-sp-ingredient-card-learn')
      const desc = c.querySelector('.js-sp-ingredient-card-desc')
      if (learnBtn) learnBtn.setAttribute('aria-expanded', 'false')
      if (desc) desc.setAttribute('aria-hidden', 'true')
    })
  }

  handleLearnClick(event) {
    const button = event.currentTarget
    const card = button.closest('.js-sp-ingredient-card')
    if (!card) return

    const willOpen = !card.classList.contains('is-active')

    this.deactivateAllCards()

    if (willOpen) {
      card.classList.add('is-active')
      button.setAttribute('aria-expanded', 'true')
      const desc = card.querySelector('.js-sp-ingredient-card-desc')
      if (desc) desc.setAttribute('aria-hidden', 'false')
    }
  }

  handleCloseCard() {
    this.deactivateAllCards()
  }

  handleOpenModal(event) {
    if (!this.modal) return
    event.preventDefault()

    this.modal.hidden = false
    this.modal.classList.add('is-active')
    this.modal.setAttribute('aria-hidden', 'false')
    document.documentElement.classList.add('scroll-lock')

    document.addEventListener('keydown', this.handleKeydown)

    this.lastFocused = document.activeElement
    const closeBtn = this.modal.querySelector(
      '.sp-ingredient-full-list-modal__close'
    )
    if (closeBtn) closeBtn.focus()
  }

  handleCloseModal() {
    if (!this.modal) return

    this.modal.classList.remove('is-active')
    this.modal.setAttribute('aria-hidden', 'true')
    this.modal.hidden = true
    document.documentElement.classList.remove('scroll-lock')

    document.removeEventListener('keydown', this.handleKeydown)

    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus()
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape' || event.code === 'Escape') {
      this.handleCloseModal()
    }
  }
}

if (!customElements.get('hh-sp-ingredient-list')) {
  customElements.define('hh-sp-ingredient-list', HHSpIngredientList)
}
